import type { Replay } from "../game/replay";
import { TICK_MS } from "../sim";
import { el } from "./dom";

const KIND_LABELS: Record<Replay["kind"], string> = {
	local: "Two players",
	ai: "Versus computer",
	online: "Online",
	survival: "Asteroid survival",
};

export interface ReplaysPageOptions {
	replays: Replay[];
	mapName: (replay: Replay) => string;
	onWatch: (index: number) => void;
	onDownload: (index: number) => void;
	onDelete: (index: number) => void;
	/** A replay file the player picked (text content). */
	onOpenFile: (text: string) => void;
}

/** The Replays page (P10.6): matches kept in this browser plus "Open replay file…". */
export function replaysContent(options: ReplaysPageOptions): HTMLElement {
	const rows = options.replays.map((r, i) =>
		el(
			"li",
			{ class: "replay-row" },
			el(
				"div",
				{ class: "replay-info" },
				el("strong", {}, `${KIND_LABELS[r.kind]} · ${options.mapName(r)}`),
				el(
					"span",
					{},
					`${r.pilots.join(" vs ")} · ${formatLength(r.ticks)} · ${formatDate(r.date)}`,
				),
			),
			el(
				"button",
				{ class: "btn btn-small", type: "button", onclick: () => options.onWatch(i) },
				"Watch",
			),
			el(
				"button",
				{ class: "btn btn-small", type: "button", onclick: () => options.onDownload(i) },
				"Save file",
			),
			el(
				"button",
				{ class: "btn btn-small", type: "button", onclick: () => options.onDelete(i) },
				"Delete",
			),
		),
	);
	const file = el("input", {
		type: "file",
		accept: ".json,application/json",
		class: "replay-file",
	});
	file.addEventListener("change", () => {
		const picked = file.files?.[0];
		if (picked) void picked.text().then(options.onOpenFile);
		file.value = "";
	});
	const open = el(
		"button",
		{ class: "btn btn-small", type: "button", onclick: () => file.click() },
		"Open replay file…",
	);
	return el(
		"div",
		{ class: "replays" },
		rows.length > 0
			? el("ul", { class: "replay-list" }, ...rows)
			: el(
					"p",
					{ class: "page-text" },
					"No replays yet. Every match you finish is kept here (the last five).",
				),
		el("div", { class: "replay-actions" }, open, file),
		el(
			"p",
			{ class: "page-text" },
			"While watching: Space pauses, ↑/↓ change the speed, ←/→ jump 10 seconds.",
		),
	);
}

function formatLength(ticks: number): string {
	const seconds = Math.floor((ticks * TICK_MS) / 1000);
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? ""
		: d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
